import { css } from '@emotion/css';
import GraphemeSplitter from 'grapheme-splitter';
import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportCategory } from '../../../models/ReportCategory';
import { FormalConfirmButton } from '../buttons/FormalConfirmButton';

const reportCategories: Array<{ text: string; categoryId: ReportCategory }> = [
  { text: 'spam_reason_04', categoryId: 'commercial' },
  { text: 'spam_reason_01', categoryId: 'criminal' },
  { text: 'spam_reason_03', categoryId: 'harassment' },
  { text: 'spam_reason_02', categoryId: 'immoral' },
  { text: 'spam_reason_00', categoryId: 'personal' },
  { text: 'spam_reason_05', categoryId: 'sexually_explicit' },
  { text: 'spam_reason_07', categoryId: 'cheating' },
  { text: 'spam_reason_06', categoryId: 'other' }
];

const textCountLimit = 400;
const splitter = new GraphemeSplitter();

export interface ReportFormProps {
  isLoading?: boolean;
  onSubmit: (category: ReportCategory, reason: string) => void;
}

export const ReportForm: React.FC<ReportFormProps> = ({ isLoading, onSubmit }) => {
  const { t } = useTranslation('best_friends');
  const [category, setCategory] = useState<ReportCategory | undefined>();
  const [reasonText, setReasonText] = useState('');
  const [reasonTextCount, setReasonTextCount] = useState(0);
  const [isTooLong, setIsTooLong] = useState(false);

  useEffect(() => {
    const count = splitter.countGraphemes(reasonText);
    setReasonTextCount(count);
    setIsTooLong(count > textCountLimit);
  }, [reasonText, setReasonTextCount, setIsTooLong]);

  const handleRadioClick = (ev: ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value as ReportCategory;
    setCategory(value);
  };

  const handleTextChange = (ev: ChangeEvent<HTMLTextAreaElement>) => {
    const value = ev.target.value;
    setReasonText(value);
  };

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    if (!category) {
      return;
    }
    onSubmit(category, reasonText);
  };

  const isButtonDisabled = !category || isTooLong;

  return (
    <form
      className={css`
        padding: 0 20px 72px;
        max-width: 480px;
        margin: 0 auto;
        h3 {
          margin: 12px 0 8px;
          color: #938666;
          font-size: 14px;
        }
        ul {
          margin: 0;
          padding: 0;
        }
        li {
          list-style-type: none;
        }
        textarea {
          color: #4d3906;
          width: 100%;
          height: 8em;
          font-size: 16px;
          font-weight: normal;
          padding: 0.4em;
          border: 1px solid #d5d5d5;
          border-radius: 8px;
          resize: none;
          outline: none;
          user-select: text;
        }
        textarea::placeholder {
          color: #b7b7b7;
        }
        input[type='radio'] {
          flex: 0 0 auto;
          appearance: none;
          border-radius: 50%;
          width: 1.5em;
          height: 1.5em;
          outline: none;
          border: 1px solid #897a66;
          padding: 3px;
          margin: 0 12px 0 0;
        }
        input[type='radio']:checked {
          background-clip: content-box;
          background-color: #50b163;
          transition: background-color 0.1s;
        }
        .reason {
          display: flex;
          flex-direction: column;
          margin: 20px 0 32px;
        }
        .reason label {
          display: flex;
          align-items: center;
          color: #493a31;
          margin: 0.5em 0;
          padding: 0.5em 0;
        }
        .description {
          margin: 12px 0 16px;
          display: flex;
          flex-direction: column;
        }
        .description label {
          font-size: 12px;
          color: #b7b7b7;
          margin-left: auto;
        }
        .warning {
          color: #e60012;
        }
        .error {
          color: #e60012;
          text-align: center;
          padding: 12px 0;
        }
        .caution {
          color: #938666;
          margin: 16px 0 32px;
          font-size: 12px;
        }
      `}
      onSubmit={handleSubmit}
    >
      <div className="reason">
        <h3>{t('report_spam_reason')}</h3>
        <ul>
          {reportCategories.map(({ categoryId, text }) => (
            <li key={categoryId}>
              <label>
                <input type="radio" name="reason" value={categoryId} onChange={handleRadioClick} />
                {t(text)}
              </label>
            </li>
          ))}
        </ul>
      </div>
      <div className="description">
        <h3>{t('report_spam_detail')}</h3>
        <textarea
          name="text"
          placeholder={t('report_spam_placeholder')}
          onChange={handleTextChange}
          // Safariでソフトウェアキーボードが非表示になったときにスクロール位置を戻すworkaround
          onBlur={() => window.scrollTo(0, 0)}
          maxLength={5000}
        />
        <label
          aria-label={`${t('aria_text_count', {
            limit: textCountLimit,
            input: reasonTextCount
          })}`}
        >
          <span className={`${isTooLong ? 'warning' : ''}`}>{reasonTextCount}</span>/
          {textCountLimit}
        </label>
      </div>
      <div className="caution">
        <p>{t('report_caution')}</p>
      </div>
      <FormalConfirmButton type="submit" isDisabled={isButtonDisabled} isLoading={isLoading}>
        {t('button_send_report')}
      </FormalConfirmButton>
    </form>
  );
};
